/**
 * Compare a warehouse driver's error code to a known one (ACT2-1116).
 *
 * Snowflake reports vendor codes as zero-padded strings (`'002003'`), and another path may give the
 * same code as a number (`2003`) or unpadded (`'2003'`). Leading zeros are stripped on both sides, so
 * all three match. An absent code never matches.
 */
export function sameErrorCode(actual: string | number | null | undefined, expected: string | number): boolean {
  if (actual === null || actual === undefined) return false;
  const normalize = (code: string | number): string =>
    String(code)
      .trim()
      .replace(/^0+(?=.)/, '');
  return normalize(actual) === normalize(expected);
}

/** Snowflake's "object does not exist or not authorized", which a role with no access to a database gets. */
export const SNOWFLAKE_OBJECT_NOT_AUTHORIZED = '2003';

/**
 * Snowflake's "This session does not have a current database". Lookout's Test runs an unqualified
 * `INFORMATION_SCHEMA` query, and a role with no USAGE on the connection's database gets this (code
 * `'090105'`, seen live), not 2003: Snowflake silently leaves the session without a database.
 */
export const SNOWFLAKE_NO_CURRENT_DATABASE = '90105';

/**
 * Snowflake's "No active warehouse selected in the current session" (`'000606'`). The session's
 * warehouse is silently left unset when the role has no USAGE on it (or it does not exist), so the
 * Test query fails with this rather than a permission error.
 */
export const SNOWFLAKE_NO_ACTIVE_WAREHOUSE = '606';
